import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  Avatar,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  InputAdornment,
  Collapse,
  Popover,
  Chip,
  CircularProgress,
  Autocomplete,
  Divider,
  Menu,
  MenuItem,
  Stack,
  Tabs,
  Tab,
  Grid
} from '@mui/material';
import {
  Send as SendIcon,
  PersonAdd as PersonAddIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  EmojiEmotions as EmojiIcon,
  AttachFile as AttachFileIcon,
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  Code as CodeIcon,
  Movie as MovieIcon,
  Tag as TagIcon,
  Lock as LockIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  NotificationsOff as NotificationsOffIcon,
  ExitToApp as ExitToAppIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  AddReaction as AddReactionIcon,
  Reply as ReplyIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Videocam as CamIcon,
  VideocamOff as CamOffIcon,
  ScreenShare as ShareIcon,
  StopScreenShare as StopShareIcon,
  CallEnd as CallEndIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { FEATURE_FLAGS } from '../config';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { styled, useTheme, alpha } from '@mui/material/styles';
import { io } from 'socket.io-client';

// Styled components
const StyledSidebar = styled(Box)(({ theme }) => {
  const lightBg = '#e3f2ff';
  const darkBg = '#10141d';
  const lightText = '#0f172a';
  const textColor = theme.palette.mode === 'dark' ? theme.palette.common.white : lightText;
  const secondaryTextColor = theme.palette.mode === 'dark'
    ? alpha(theme.palette.common.white, 0.7)
    : alpha(lightText, 0.65);
  const hoverBg = theme.palette.mode === 'dark'
    ? alpha(theme.palette.common.white, 0.12)
    : alpha(lightText, 0.08);
  const selectedBg = theme.palette.mode === 'dark'
    ? alpha(theme.palette.common.white, 0.2)
    : alpha(lightText, 0.14);
  const selectedHoverBg = theme.palette.mode === 'dark'
    ? alpha(theme.palette.common.white, 0.3)
    : alpha(lightText, 0.2);

  return {
    backgroundColor: theme.palette.mode === 'dark' ? darkBg : lightBg,
    borderRight: `1px solid ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.4 : 0.06)}`,
    height: '100%',
    color: textColor,
    '& .MuiListItem-root': {
      paddingTop: theme.spacing(0.75),
      paddingBottom: theme.spacing(0.75),
      borderRadius: theme.shape.borderRadius,
      marginInline: theme.spacing(0.75),
      paddingInline: theme.spacing(1),
      transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
      '&:hover': {
        backgroundColor: hoverBg,
      },
      '&.Mui-selected': {
        backgroundColor: selectedBg,
        boxShadow: `inset 0 0 0 1px ${alpha(textColor, theme.palette.mode === 'dark' ? 0.3 : 0.35)}`,
        '&:hover': {
          backgroundColor: selectedHoverBg,
        },
      },
      '& .MuiListItemText-primary': {
        color: textColor,
      },
      '& .MuiListItemText-secondary': {
        color: secondaryTextColor,
      }
    },
    '& .MuiTypography-root': {
      color: textColor,
    },
    '& .MuiSvgIcon-root': {
      color: theme.palette.mode === 'dark' ? theme.palette.common.white : lightText,
    }
  };
});

const MessageInput = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius * 2,
    backgroundColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.background.paper, 0.9)
      : theme.palette.background.paper,
    transition: 'all 0.2s ease',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.background.paper, 0.95)
        : theme.palette.background.paper,
      boxShadow: theme.palette.mode === 'dark' ? '0 1px 6px rgba(0,0,0,0.4)' : '0 1px 4px rgba(0,0,0,0.04)'
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.background.paper, 0.98)
        : theme.palette.background.paper,
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.12)}`
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.35 : 0.4),
      transition: 'border-color 0.2s ease'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.4 : 0.5)
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
      borderWidth: 1
    }
  },
}));

const MessageBubble = styled(Paper, { shouldForwardProp: (prop) => prop !== 'isCurrentUser' })(({ theme, isCurrentUser }) => {
  const isDark = theme.palette.mode === 'dark';
  const baseBg = isDark ? '#121723' : '#ffffff';
  const selfBg = isDark ? alpha(theme.palette.primary.main, 0.24) : '#eef2ff';
  const primaryInk = '#0f172a';
  const borderColor = isDark
    ? alpha(theme.palette.common.white, isCurrentUser ? 0.22 : 0.12)
    : alpha(primaryInk, isCurrentUser ? 0.14 : 0.08);
  const shadow = isDark
    ? '0 10px 28px rgba(0,0,0,0.45)'
    : '0 12px 34px rgba(15,23,42,0.12)';

  return {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.25),
    backgroundColor: isCurrentUser ? selfBg : baseBg,
    color: theme.palette.text.primary,
    display: 'inline-block',
    maxWidth: '68%',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '94%',
    },
    borderRadius: theme.shape.borderRadius * 1.6,
    border: `1px solid ${borderColor}`,
    boxShadow: shadow,
    position: 'relative',
    transition: 'background-color 0.2s ease, transform 0.2s ease, box-shadow 0.25s ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: isDark ? '0 14px 36px rgba(0,0,0,0.55)' : '0 18px 38px rgba(15,23,42,0.16)',
      backgroundColor: isCurrentUser
        ? (isDark ? alpha(theme.palette.primary.main, 0.3) : '#e0e7ff')
        : (isDark ? '#1a2030' : '#f7fafc')
    },
    '&:before, &:after': {
      display: 'none'
    },
    '& .message-header': {
      marginBottom: theme.spacing(0.25),
      fontWeight: 500,
      display: 'flex',
      justifyContent: 'space-between',
      flexWrap: 'nowrap',
    },
    '& .message-time': {
      color: theme.palette.text.secondary,
    },
    '& .message-content': {
      wordBreak: 'break-word',
      lineHeight: 1.5,
      color: theme.palette.text.primary
    },
    '& .mention': {
      color: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      padding: '2px 4px',
      borderRadius: '4px',
      fontWeight: 500,
    },
    '& .error-message': {
      color: theme.palette.error.main,
      fontSize: '0.75rem',
      marginTop: theme.spacing(0.5),
    },
  };
});

function Messaging() {
  const [conversations, setConversations] = useState({ channels: [], directMessages: [] });
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [crewMembers, setCrewMembers] = useState([]);
  const [newConversationOpen, setNewConversationOpen] = useState(false);
  const [addMembersOpen, setAddMembersOpen] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [conversationName, setConversationName] = useState('');
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [channelsOpen, setChannelsOpen] = useState(true);
  const [directMessagesOpen, setDirectMessagesOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [mentionAnchorEl, setMentionAnchorEl] = useState(null);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionPosition, setMentionPosition] = useState({ start: 0, end: 0 });
  const [showAddMemberPrompt, setShowAddMemberPrompt] = useState(false);
  const [selectedMention, setSelectedMention] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMembersForChat, setSelectedMembersForChat] = useState([]);
  const [showStartChatDialog, setShowStartChatDialog] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [availableCrewMembers, setAvailableCrewMembers] = useState([]);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
  const [emojiCategory, setEmojiCategory] = useState('smileys');
  const [emojiSearch, setEmojiSearch] = useState('');
  const [pendingAttachment, setPendingAttachment] = useState(null); // { file, url }
  const attachInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const emojiCategories = useMemo(() => ({
    smileys: [
      { char: '😀', label: 'Grinning Face', keywords: ['grin', 'smile'] },
      { char: '😄', label: 'Beaming Face', keywords: ['grin', 'happy'] },
      { char: '😁', label: 'Beaming Eyes', keywords: ['cheerful'] },
      { char: '😂', label: 'Face with Tears of Joy', keywords: ['laugh', 'lol'] },
      { char: '😊', label: 'Smiling Eyes', keywords: ['warm', 'smile'] },
      { char: '😍', label: 'Heart Eyes', keywords: ['love', 'admire'] },
      { char: '🤩', label: 'Star-Struck', keywords: ['amazed', 'wow'] },
      { char: '😎', label: 'Cool Face', keywords: ['cool', 'chill'] },
      { char: '🤔', label: 'Thinking Face', keywords: ['ponder', 'hmm'] },
      { char: '😴', label: 'Sleeping Face', keywords: ['tired', 'zzz'] },
      { char: '🥳', label: 'Party Face', keywords: ['celebrate', 'party'] },
      { char: '🤯', label: 'Mind Blown', keywords: ['shocked', 'wow'] }
    ],
    gestures: [
      { char: '👍', label: 'Thumbs Up', keywords: ['ok', 'approve'] },
      { char: '👎', label: 'Thumbs Down', keywords: ['disapprove', 'no'] },
      { char: '👏', label: 'Clapping Hands', keywords: ['applause', 'congrats'] },
      { char: '🙌', label: 'Raised Hands', keywords: ['praise', 'high five'] },
      { char: '🙏', label: 'Folded Hands', keywords: ['thanks', 'prayer'] },
      { char: '🤝', label: 'Handshake', keywords: ['deal', 'agreement'] },
      { char: '💪', label: 'Flexed Biceps', keywords: ['strong', 'power'] },
      { char: '👋', label: 'Waving Hand', keywords: ['hello', 'goodbye'] },
      { char: '✌️', label: 'Victory Hand', keywords: ['peace', 'victory'] },
      { char: '🤘', label: 'Rock On', keywords: ['rock', 'music'] },
      { char: '🤞', label: 'Crossed Fingers', keywords: ['hope', 'luck'] },
      { char: '🫶', label: 'Heart Hands', keywords: ['love', 'support'] }
    ],
    nature: [
      { char: '🌟', label: 'Glowing Star', keywords: ['bright', 'shine'] },
      { char: '✨', label: 'Sparkles', keywords: ['magic', 'shine'] },
      { char: '🌈', label: 'Rainbow', keywords: ['colorful', 'hope'] },
      { char: '🌸', label: 'Cherry Blossom', keywords: ['flower', 'spring'] },
      { char: '🌿', label: 'Herb', keywords: ['plant', 'green'] },
      { char: '🔥', label: 'Fire', keywords: ['hot', 'lit'] },
      { char: '💧', label: 'Droplet', keywords: ['water', 'calm'] },
      { char: '❄️', label: 'Snowflake', keywords: ['cold', 'winter'] },
      { char: '☀️', label: 'Sun', keywords: ['warm', 'bright'] },
      { char: '🌙', label: 'Crescent Moon', keywords: ['night', 'calm'] },
      { char: '🌻', label: 'Sunflower', keywords: ['happy', 'flower'] },
      { char: '🪴', label: 'Potted Plant', keywords: ['plant', 'home'] }
    ],
    activities: [
      { char: '🎬', label: 'Clapper Board', keywords: ['film', 'movie'] },
      { char: '🎧', label: 'Headphones', keywords: ['music', 'audio'] },
      { char: '🎨', label: 'Artist Palette', keywords: ['creative', 'art'] },
      { char: '🎯', label: 'Direct Hit', keywords: ['target', 'focus'] },
      { char: '🎉', label: 'Party Popper', keywords: ['celebrate', 'party'] },
      { char: '🎮', label: 'Video Game Controller', keywords: ['game', 'play'] },
      { char: '🏆', label: 'Trophy', keywords: ['win', 'award'] },
      { char: '📚', label: 'Books', keywords: ['study', 'learn'] },
      { char: '📝', label: 'Memo', keywords: ['notes', 'write'] },
      { char: '📸', label: 'Camera', keywords: ['photo', 'capture'] },
      { char: '🎵', label: 'Musical Note', keywords: ['music', 'song'] },
      { char: '🚀', label: 'Rocket', keywords: ['launch', 'startup'] }
    ],
    symbols: [
      { char: '❤️', label: 'Red Heart', keywords: ['love', 'heart'] },
      { char: '💛', label: 'Yellow Heart', keywords: ['friend', 'love'] },
      { char: '💙', label: 'Blue Heart', keywords: ['trust', 'loyal'] },
      { char: '💜', label: 'Purple Heart', keywords: ['care', 'support'] },
      { char: '🧠', label: 'Brain', keywords: ['idea', 'smart'] },
      { char: '💡', label: 'Light Bulb', keywords: ['idea', 'think'] },
      { char: '⚡', label: 'High Voltage', keywords: ['fast', 'energy'] },
      { char: '✅', label: 'Check Mark', keywords: ['done', 'complete'] },
      { char: '❗', label: 'Exclamation Mark', keywords: ['important', 'alert'] },
      { char: '❓', label: 'Question Mark', keywords: ['question', 'help'] },
      { char: '♻️', label: 'Recycling Symbol', keywords: ['sustain', 'reuse'] },
      { char: '🔒', label: 'Locked', keywords: ['secure', 'lock'] }
    ]
  }), []);

  const filteredEmojis = useMemo(() => {
    const current = emojiCategories[emojiCategory] || [];
    if (!emojiSearch.trim()) return current;
    const term = emojiSearch.trim().toLowerCase();
    return current.filter(({ label, keywords }) => {
      if (label.toLowerCase().includes(term)) return true;
      return (keywords || []).some((kw) => kw.includes(term));
    });
  }, [emojiCategories, emojiCategory, emojiSearch]);

  // WebRTC/Signaling state
  const [inCall, setInCall] = useState(false);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isCamOn, setIsCamOn] = useState(true);
  const [isSharing, setIsSharing] = useState(false);
  const [showSelfView, setShowSelfView] = useState(true);
  const socketRef = useRef(null);
  const watchSocketRef = useRef(null); // passive socket for room notifications
  const localVideoRef = useRef(null);
  const localStreamRef = useRef(null);
  const pcsRef = useRef({}); // peerId -> RTCPeerConnection
  const [remoteStreams, setRemoteStreams] = useState({}); // peerId -> MediaStream
  const [callPeers, setCallPeers] = useState({}); // peerId -> true for current room
  const makingOfferRef = useRef({}); // peerId -> boolean
  const [popoutEnabled, setPopoutEnabled] = useState(() => {
    const saved = localStorage.getItem('vc_popout');
    return saved === null ? true : saved === 'true';
  });

  const ICE_SERVERS = useRef([
    { urls: 'stun:stun.l.google.com:19302' },
  ]);
  const SERVER_URL = useRef(process.env.REACT_APP_SERVER_URL || 'http://localhost:5002');
  const SHOW_LEGACY_CALL_PANEL = false;

  // Keyboard shortcuts for controls (Team view only handles Esc to leave)
  useEffect(() => {
    const handler = (e) => {
      if (e.altKey || e.metaKey || e.ctrlKey) return;
      const k = e.key.toLowerCase();
      if (k === 'escape' && inCall) { e.preventDefault(); leaveCall(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [inCall]);

  const handleDeleteMessage = async (messageId) => {
    if (!selectedConversation || !messageId) return;
    setMessages(prev => prev.map(m => m._id === messageId ? { ...m, deleting: true } : m));
    try {
      await axios.delete(`/api/v1/messages/${selectedConversation._id}/${messageId}`);
      setMessages(prev => prev.filter(m => m._id !== messageId));
    } catch (error) {
      console.error('Error deleting message:', error);
      setMessages(prev => prev.map(m => m._id === messageId ? { ...m, deleting: false, error: true, errorMessage: 'Failed to delete' } : m));
    }
  };

  // Invite helpers
  const emitInvite = () => {
    const room = selectedConversation?._id;
    if (!room) return;
    const payload = { room, inviterId: user?._id, inviterName: user?.name };
    if (watchSocketRef.current && watchSocketRef.current.connected) {
      watchSocketRef.current.emit('rtc:invite', payload);
      return;
    }
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit('rtc:invite', payload);
      return;
    }
    // Fallback: ephemeral socket just to emit invite
    try {
      const temp = io(`${SERVER_URL.current}/rtc`, { transports: ['websocket'], withCredentials: true });
      temp.on('connect', () => {
        temp.emit('rtc:watch', { room });
        setTimeout(() => { temp.emit('rtc:invite', payload); temp.disconnect(); }, 50);
      });
    } catch {}
  };

  const sendMessageAndInvite = () => {
    handleSendMessage();
    emitInvite();
  };

  const openCallWindow = () => {
    if (!FEATURE_FLAGS.messagesVideoCall) return;
    if (!selectedConversation?._id) return;
    const features = 'width=1120,height=720,resizable,scrollbars';
    const url = `${window.location.origin}/call?conv=${encodeURIComponent(selectedConversation._id)}`;
    // If already in an inline call, leave it to prevent duplicate presence
    if (inCall) {
      try { leaveCall(); } catch {}
    }
    window.open(url, 'callWindow', features);
  };

  // Passive socket: subscribe to invites for the selected conversation
  useEffect(() => {
    try {
      // disconnect previous watcher
      if (watchSocketRef.current) {
        try { watchSocketRef.current.disconnect(); } catch {}
        watchSocketRef.current = null;
      }
      if (!selectedConversation?._id) return;
      const ws = io(`${SERVER_URL.current}/rtc`, { transports: ['websocket'], withCredentials: true });
      watchSocketRef.current = ws;
      ws.on('connect', () => {
        ws.emit('rtc:watch', { room: selectedConversation._id });
      });
      ws.on('rtc:invite', ({ inviterId, inviterName, topic, at }) => {
        setMessages(prev => ([
          ...prev,
          {
            _id: `invite-${at}-${Math.random().toString(36).slice(2,7)}`,
            type: 'invite',
            inviterId: inviterId || 'unknown',
            inviterName: inviterName || 'Someone',
            topic: topic || null,
            createdAt: new Date(at || Date.now()).toISOString(),
            sender: { _id: inviterId || 'system', name: inviterName || 'System' },
          }
        ]));
      });
      ws.on('rtc:invite-response', ({ responderId, responderName, accepted, at }) => {
        const txt = accepted ? `${responderName || 'Someone'} joined the call` : `${responderName || 'Someone'} declined the invite`;
        setMessages(prev => ([
          ...prev,
          { _id: `sys-${at}-${Math.random().toString(36).slice(2,7)}`, content: txt, type: 'system', createdAt: new Date(at || Date.now()).toISOString(), sender: { _id: 'system', name: 'System' } }
        ]));
      });
      ws.on('disconnect', () => {});
      return () => { try { ws.disconnect(); } catch {} };
    } catch {}
  }, [selectedConversation, SERVER_URL.current]);

  // Video tile helper
  const VideoTile = ({ stream, label, muted, highlight }) => {
    const ref = useRef(null);
    useEffect(() => {
      if (ref.current && stream) {
        ref.current.srcObject = stream;
        ref.current.muted = !!muted;
        ref.current.playsInline = true;
        ref.current.autoplay = true;
        ref.current.play?.().catch(() => {});
      }
    }, [stream, muted]);
    return (
      <Box sx={{
        position: 'relative',
        width: { xs: '100%', sm: 260, md: 280 },
        aspectRatio: '16 / 9',
        bgcolor: '#000',
        borderRadius: 1.25,
        overflow: 'hidden',
        border: (t) => `1px solid ${alpha(t.palette.divider, 0.5)}`,
        boxShadow: (t) => highlight ? `0 0 0 2px ${alpha(t.palette.primary.main, 0.6)} inset` : t.shadows[1],
      }}>
        <video ref={ref} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <Box sx={{ position: 'absolute', left: 8, bottom: 8, bgcolor: (t) => alpha(t.palette.common.black, 0.4), color: '#fff', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.75rem' }}>
          {label}
        </Box>
      </Box>
    );
  };

  const attachLocalStream = async () => {
    if (localStreamRef.current) return localStreamRef.current;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
    localStreamRef.current = stream;
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = stream;
      localVideoRef.current.muted = true;
      await localVideoRef.current.play().catch(() => {});
    }
    return stream;
  };

  const createPeerConnection = (peerId) => {
    if (pcsRef.current[peerId]) return pcsRef.current[peerId];
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS.current });
    // Pre-create transceivers in deterministic order to keep m-line order stable
    try {
      pc.addTransceiver('audio', { direction: 'sendrecv' });
      pc.addTransceiver('video', { direction: 'sendrecv' });
    } catch {}
    pc.onicecandidate = (e) => {
      if (e.candidate && socketRef.current) {
        socketRef.current.emit('rtc:ice', { to: peerId, candidate: e.candidate });
      }
    };
    pc.ontrack = (e) => {
      const [stream] = e.streams;
      setRemoteStreams(prev => ({ ...prev, [peerId]: stream }));
    };
    // mark local tracks state to avoid duplicate addTrack
    pc.__localTracksAdded = false;
    pcsRef.current[peerId] = pc;
    return pc;
  };

  const ensureLocalTracksAdded = (pc) => {
    try {
      if (!pc || pc.__localTracksAdded) return;
      const stream = localStreamRef.current;
      if (!stream) return;
      const senders = (pc.getSenders && pc.getSenders()) || [];
      const aTrack = stream.getAudioTracks()[0] || null;
      const vTrack = stream.getVideoTracks()[0] || null;
      const aSender = senders.find(s => (s.track && s.track.kind === 'audio')) || senders.find(s => s.receiver && s.receiver.track && s.receiver.track.kind === 'audio');
      const vSender = senders.find(s => (s.track && s.track.kind === 'video')) || senders.find(s => s.receiver && s.receiver.track && s.receiver.track.kind === 'video');
      if (aSender && aTrack) { try { aSender.replaceTrack(aTrack); } catch {} }
      if (vSender && vTrack) { try { vSender.replaceTrack(vTrack); } catch {} }
      pc.__localTracksAdded = true;
    } catch {}
  };

  const cleanupPeer = (peerId) => {
    const pc = pcsRef.current[peerId];
    if (pc) {
      try { pc.onicecandidate = null; pc.ontrack = null; pc.close(); } catch {}
      delete pcsRef.current[peerId];
    }
    setRemoteStreams(prev => {
      const next = { ...prev };
      delete next[peerId];
      return next;
    });
  };

  const leaveCall = () => {
    setInCall(false);
    // Close PCs
    Object.keys(pcsRef.current).forEach(pid => cleanupPeer(pid));
    // Stop local stream
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    // Disconnect socket
    try { socketRef.current?.disconnect(); } catch {}
    socketRef.current = null;
    setIsSharing(false);
    setCallPeers({});
  };

  const joinCall = async () => {
    if (!FEATURE_FLAGS.messagesVideoCall) return;
    if (!selectedConversation?._id) return;
    try {
      const stream = await attachLocalStream();
      // Respect current toggles
      stream.getAudioTracks().forEach(t => (t.enabled = isMicOn));
      stream.getVideoTracks().forEach(t => (t.enabled = isCamOn));

      const s = io(`${SERVER_URL.current}/rtc`, {
        transports: ['websocket'],
        withCredentials: true,
      });
      socketRef.current = s;

      s.on('connect', () => {
        s.emit('rtc:join', { room: selectedConversation._id, userId: user?._id });
      });

      s.on('rtc:peers', async ({ peers }) => {
        const initial = {};
        for (const pid of (peers || [])) {
          initial[pid] = true;
          const pc = createPeerConnection(pid);
          // add local tracks once, guarded
          ensureLocalTracksAdded(pc);
          if (pc.signalingState === 'stable') {
            makingOfferRef.current[pid] = true;
            try {
              const offer = await pc.createOffer();
              await pc.setLocalDescription(offer);
              s.emit('rtc:offer', { to: pid, sdp: offer });
            } finally {
              makingOfferRef.current[pid] = false;
            }
          }
        }
        setCallPeers(initial);
      });

      s.on('rtc:user-joined', async ({ socketId }) => {
        // Proactively create an offer to the new peer
        const pid = socketId;
        if (!pid) return;
        setCallPeers(prev => ({ ...prev, [pid]: true }));
        // System message: user joined
        setMessages(prev => ([
          ...prev,
          { _id: `sys-${Date.now()}`, content: `A participant joined the call`, type: 'system', createdAt: new Date().toISOString(), sender: { _id: 'system', name: 'System' } }
        ]));
        const pc = createPeerConnection(pid);
        ensureLocalTracksAdded(pc);
        if (pc.signalingState === 'stable') {
          makingOfferRef.current[pid] = true;
          try {
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            s.emit('rtc:offer', { to: pid, sdp: offer });
          } finally {
            makingOfferRef.current[pid] = false;
          }
        }
      });

      s.on('rtc:offer', async ({ from, sdp }) => {
        const pc = createPeerConnection(from);
        // Ensure local tracks added before setting local answer (guarded)
        ensureLocalTracksAdded(pc);
        // Handle glare/offer collision
        const offer = new RTCSessionDescription(sdp);
        if (offer.type !== 'offer') return;
        const collision = makingOfferRef.current[from] || pc.signalingState !== 'stable';
        if (collision) {
          try { await pc.setLocalDescription({ type: 'rollback' }); } catch {}
        }
        await pc.setRemoteDescription(offer);
        if (pc.signalingState !== 'have-remote-offer') return;
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        s.emit('rtc:answer', { to: from, sdp: answer });
      });

      s.on('rtc:answer', async ({ from, sdp }) => {
        const pc = pcsRef.current[from];
        if (!pc) return;
        // Only set remote answer if we are in have-local-offer state
        if (pc.signalingState !== 'have-local-offer') return;
        await pc.setRemoteDescription(new RTCSessionDescription(sdp));
      });

      s.on('rtc:ice', async ({ from, candidate }) => {
        const pc = pcsRef.current[from];
        if (!pc) return;
        try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); } catch {}
      });

      s.on('rtc:user-left', ({ socketId }) => {
        cleanupPeer(socketId);
        setCallPeers(prev => {
          const next = { ...prev };
          delete next[socketId];
          return next;
        });
        // System message: user left
        setMessages(prev => ([
          ...prev,
          { _id: `sys-${Date.now()}`, content: `A participant left the call`, type: 'system', createdAt: new Date().toISOString(), sender: { _id: 'system', name: 'System' } }
        ]));
      });

      s.on('disconnect', () => {
        leaveCall();
      });

      setInCall(true);
      // System message: you joined
      setMessages(prev => ([
        ...prev,
        { _id: `sys-${Date.now()}`, content: `You joined the call`, type: 'system', createdAt: new Date().toISOString(), sender: { _id: 'system', name: 'System' } }
      ]));
    } catch (e) {
      console.error('Join call failed', e);
      leaveCall();
    }
  };

  // Emoji handlers
  const handleEmojiButtonClick = (event) => {
    setEmojiAnchorEl(event.currentTarget);
  };

  const handleEmojiClose = () => {
    setEmojiAnchorEl(null);
  };

  const handleEmojiSelect = (emoji) => {
    setNewMessage((prev) => (prev || '') + emoji);
    setEmojiAnchorEl(null);
  };

  const handleAttachClick = () => {
    attachInputRef.current?.click();
  };

  const handleAttachmentChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPendingAttachment({ file, url });
  };

  const removePendingAttachment = () => {
    if (pendingAttachment?.url) {
      try { URL.revokeObjectURL(pendingAttachment.url); } catch {}
    }
    setPendingAttachment(null);
    if (attachInputRef.current) attachInputRef.current.value = '';
  };

  const toggleMic = () => {
    const next = !isMicOn;
    setIsMicOn(next);
    if (localStreamRef.current) localStreamRef.current.getAudioTracks().forEach(t => (t.enabled = next));
  };
  const toggleCam = () => {
    const next = !isCamOn;
    setIsCamOn(next);
    if (localStreamRef.current) localStreamRef.current.getVideoTracks().forEach(t => (t.enabled = next));
  };
  const shareScreen = async () => {
    try {
      if (!inCall) return;
      if (!isSharing) {
        const display = await navigator.mediaDevices.getDisplayMedia({ video: true });
        const screenTrack = display.getVideoTracks()[0];
        // Replace sender video tracks for all peers
        Object.values(pcsRef.current).forEach(pc => {
          const sender = pc.getSenders().find(s => s.track && s.track.kind === 'video');
          if (sender) sender.replaceTrack(screenTrack);
        });
        setIsSharing(true);
        screenTrack.onended = () => {
          // revert to camera
          if (localStreamRef.current) {
            const camTrack = localStreamRef.current.getVideoTracks()[0];
            Object.values(pcsRef.current).forEach(pc => {
              const sender = pc.getSenders().find(s => s.track && s.track.kind === 'video');
              if (sender && camTrack) sender.replaceTrack(camTrack);
            });
          }
          setIsSharing(false);
        };
      }
    } catch (e) {
      console.error('Share screen failed', e);
    }
  };

  // Removed unused helper getStartOfDay

  // Fetch conversations and crew members
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        const [conversationsRes, crewMembersRes] = await Promise.all([
          axios.get('/api/v1/conversations'),
          axios.get('/api/v1/crew')
        ]);

        const conversationsData = conversationsRes.data.map(conv => ({
          ...conv,
          members: conv.members || [] // Ensure members is always an array
        }));

        // Separate channels and direct messages
        const channels = conversationsData.filter(conv => conv.type === 'group');
        const directMessages = conversationsData.filter(conv => conv.type === 'direct');
        
        setConversations({ channels, directMessages });
        setCrewMembers(crewMembersRes.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, [user, navigate]);

  // Fetch available crew members
  const fetchAvailableCrewMembers = async () => {
    try {
      const response = await axios.get('/api/v1/conversations/available-members');
      setAvailableCrewMembers(response.data);
    } catch (error) {
      console.error('Error fetching crew members:', error);
    }
  };

  useEffect(() => {
    fetchAvailableCrewMembers();
  }, []);

  // Fetch messages when conversation is selected
  useEffect(() => {
    if (selectedConversation) {
      const fetchMessages = async () => {
        try {
          setLoading(true);
          const res = await axios.get(`/api/v1/messages/${selectedConversation._id}`);
          setMessages(res.data);
          scrollToBottom();
        } catch (error) {
          console.error('Error fetching messages:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchMessages();
    }
  }, [selectedConversation]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if ((newMessage.trim() || pendingAttachment) && selectedConversation && !sendingMessage) {
      setSendingMessage(true);

      // If an attachment is pending, upload it to the server first to obtain a persistent URL
      let attachmentForMessage = null;
      try {
        if (pendingAttachment?.file) {
          const fd = new FormData();
          fd.append('file', pendingAttachment.file);
          const uploadRes = await axios.post('/api/v1/uploads/messages', fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          const { url, name, size, type } = uploadRes.data || {};
          if (url) {
            const normalizedType = (type && String(type).startsWith('image/')) ? 'image' : 'file';
            attachmentForMessage = { url, name, size, type: normalizedType };
          }
        }
      } catch (e) {
        console.error('Attachment upload failed, continuing with message send:', e);
      }

      const tempMessage = {
        _id: `temp-${Date.now()}`,
        content: newMessage,
        sender: {
          _id: user._id,
          name: user.name,
          avatar: user.avatar
        },
        createdAt: new Date().toISOString(),
        isTemp: true,
        attachments: (attachmentForMessage
          ? [attachmentForMessage]
          : (pendingAttachment ? [{
              name: pendingAttachment.file.name,
              size: pendingAttachment.file.size,
              type: (pendingAttachment.file.type && pendingAttachment.file.type.startsWith('image/')) ? 'image' : 'file',
              url: pendingAttachment.url
            }] : []))
      };

      setMessages(prev => [...prev, tempMessage]);
      const messageContent = newMessage;
      const contentToSend = messageContent && messageContent.trim().length > 0
        ? messageContent
        : ((attachmentForMessage || pendingAttachment) ? '[Attachment]' : '');
      setNewMessage('');
      scrollToBottom();

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        const res = await axios.post(
          `/api/v1/messages/${selectedConversation._id}`,
          { content: contentToSend, attachments: attachmentForMessage ? [attachmentForMessage] : [] },
          { signal: controller.signal }
        );

        clearTimeout(timeoutId);

        setMessages(prev => prev.map(msg => {
          if (msg._id !== tempMessage._id) return msg;
          const merged = { ...res.data, createdAt: res.data.createdAt || new Date().toISOString() };
          // If server didn't echo attachments, preserve the ones we used locally
          if ((!merged.attachments || merged.attachments.length === 0) && (tempMessage.attachments && tempMessage.attachments.length > 0)) {
            merged.attachments = tempMessage.attachments;
          }
          return merged;
        }));

        // Clear composer attachment state (the message now references the uploaded URL)
        setPendingAttachment(null);
      } catch (error) {
        console.error('Error sending message:', error);
        setMessages(prev => prev.map(msg =>
          msg._id === tempMessage._id ? {
            ...msg,
            error: true,
            errorMessage: error.code === 'ERR_CANCELED' ? 'Request timed out' : 'Failed to send'
          } : msg
        ));
      } finally {
        setSendingMessage(false);
      }
    }
  };

  const handleCreateConversation = async () => {
    try {
      // Enforce restriction: only allow members from availableCrewMembers
      const allowedIds = new Set((availableCrewMembers || []).map(m => m._id));
      const requestedIds = selectedMembers.map(member => member._id);
      const filteredIds = requestedIds.filter(id => allowedIds.has(id));
      const excluded = requestedIds.filter(id => !allowedIds.has(id));

      if (excluded.length > 0) {
        console.warn('Some selected members are not available to message and were excluded.');
      }

      const res = await axios.post('/api/v1/conversations', {
        name: conversationName,
        memberIds: filteredIds,
        projectId: user.currentProject // Assuming user has currentProject set
      });

      // Ensure the new conversation has members array
      const newConversation = {
        ...res.data,
        members: res.data.members || []
      };

      setConversations(prev => ({ 
        ...prev, 
        channels: [...prev.channels, newConversation]
      }));

      setSelectedConversation(newConversation);
      setNewConversationOpen(false);
      setConversationName('');
      setSelectedMembers([]);
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  };

  const handleAddMembers = async (memberIds) => {
    try {
      if (!selectedConversation || !memberIds.length) return;

      // Enforce restriction: only allow members from availableCrewMembers
      const allowedIds = new Set((availableCrewMembers || []).map(m => m._id));
      const filteredIds = memberIds.filter(id => allowedIds.has(id));
      const excluded = memberIds.filter(id => !allowedIds.has(id));
      if (excluded.length > 0) {
        console.warn('Some selected members are not available to message and were excluded.');
      }

      const response = await axios.post(
        `/api/v1/conversations/${selectedConversation._id}/members`,
        { memberIds: filteredIds }
      );

      // Update the conversation with new members
      setSelectedConversation(prev => ({
        ...prev,
        members: [...(prev.members || []), ...response.data.members]
      }));

      // Update conversations list
      setConversations(prev => {
        if (selectedConversation.type === 'group') {
          return {
            ...prev,
            channels: prev.channels.map(channel => 
              channel._id === selectedConversation._id 
                ? { ...channel, members: [...(channel.members || []), ...response.data.members] }
                : channel
            )
          };
        }
        return prev;
      });

      setAddMembersOpen(false);
    } catch (error) {
      console.error('Error adding members:', error);
      alert(error.response?.data?.message || 'Failed to add members to conversation');
    }
  };

  

  const handleMessageChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);

    // Check for @ mentions
    const cursorPosition = e.target.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPosition);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);

    if (mentionMatch) {
      const searchTerm = mentionMatch[1];
      setMentionSearch(searchTerm);
      setMentionPosition({
        start: cursorPosition - searchTerm.length - 1,
        end: cursorPosition
      });
      
      // Show mention popup
      setMentionAnchorEl(e.target);
    } else {
      setMentionAnchorEl(null);
    }
  };

  const handleMentionSelect = (member) => {
    if (!selectedConversation || !member) return;
    
    const conversationMembers = selectedConversation?.members || [];
    const isMemberInConversation = conversationMembers.some(m => m._id === member._id);

    if (!isMemberInConversation) {
      setSelectedMention(member);
      setShowAddMemberPrompt(true);
    } else {
      // If member is already in conversation, just add the mention
      const beforeMention = newMessage.substring(0, mentionPosition.start);
      const afterMention = newMessage.substring(mentionPosition.end);
      setNewMessage(beforeMention + '@' + member.name + ' ' + afterMention);
    }
    setMentionAnchorEl(null);
  };

  const handleAddMentionedMember = async () => {
    if (!selectedMention || !selectedConversation?._id) {
      setShowAddMemberPrompt(false);
      setSelectedMention(null);
      return;
    }

    try {
      await axios.post(`/api/v1/conversations/${selectedConversation._id}/members`, {
        memberIds: [selectedMention._id]
      });

      // Refresh conversation data
      const conversationsRes = await axios.get('/api/v1/conversations');
      
      const conversationsData = conversationsRes.data.map(conv => ({
        ...conv,
        members: conv.members || [] // Ensure members is always an array
      }));

      // Separate channels and direct messages
      const channels = conversationsData.filter(conv => conv.type === 'group');
      const directMessages = conversationsData.filter(conv => conv.type === 'direct');
      
      setConversations({ channels, directMessages });

      // Update selected conversation
      const updatedSelectedConv = conversationsData.find(conv => conv._id === selectedConversation._id);
      if (updatedSelectedConv) {
        setSelectedConversation(updatedSelectedConv);
      }

      // Add the mention to the message
      if (mentionPosition.start >= 0 && mentionPosition.end >= 0) {
        const beforeMention = newMessage.substring(0, mentionPosition.start);
        const afterMention = newMessage.substring(mentionPosition.end);
        setNewMessage(beforeMention + '@' + selectedMention.name + ' ' + afterMention);
      }
    } catch (error) {
      console.error('Error adding mentioned member:', error);
    }

    setShowAddMemberPrompt(false);
    setSelectedMention(null);
  };

  const handleDeleteConversation = async () => {
    if (!selectedConversation?._id) return;

    try {
      await axios.delete(`/api/v1/conversations/${selectedConversation._id}`);
      
      // Refresh conversations list
      const conversationsRes = await axios.get('/api/v1/conversations');
      const conversationsData = conversationsRes.data.map(conv => ({
        ...conv,
        members: conv.members || []
      }));

      // Separate channels and direct messages
      const channels = conversationsData.filter(conv => conv.type === 'group');
      const directMessages = conversationsData.filter(conv => conv.type === 'direct');
      
      setConversations({ channels, directMessages });
      setSelectedConversation(null);
      setDeleteDialogOpen(false);
      setMenuAnchorEl(null);
    } catch (error) {
      console.error('Error deleting conversation:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleLeaveConversation = async () => {
    try {
      if (!selectedConversation) return;

      const response = await axios.post(`/api/v1/conversations/${selectedConversation._id}/leave`);
      
      // Remove conversation from list
      setConversations(prev => {
        if (response.data.type === 'group') {
          return {
            ...prev,
            channels: prev.channels.filter(c => c._id !== selectedConversation._id)
          };
        } else {
          return {
            ...prev,
            directMessages: prev.directMessages.filter(dm => dm._id !== selectedConversation._id)
          };
        }
      });

      // Clear selected conversation
      setSelectedConversation(null);
      setLeaveDialogOpen(false);
    } catch (error) {
      console.error('Error leaving conversation:', error);
      alert(error.response?.data?.message || 'Failed to leave conversation');
    }
  };

  // Removed unused handleStartDirectMessage

  const handleMemberSelect = (member) => {
    if (member._id === user._id) return; // Can't select yourself
    
    setSelectedMembersForChat(prev => {
      const isSelected = prev.some(m => m._id === member._id);
      if (isSelected) {
        return prev.filter(m => m._id !== member._id);
      } else {
        return [...prev, member];
      }
    });
  };

  const handleStartChatWithSelected = async () => {
    try {
      if (selectedMembersForChat.length === 0) {
        alert("Please select members to chat with");
        return;
      }

      if (selectedMembersForChat.length === 1) {
        // For single member, create or open DM
        const existingDM = conversations?.directMessages?.find(dm => 
          dm.members.some(m => m._id === selectedMembersForChat[0]._id)
        );

        if (existingDM) {
          setSelectedConversation(existingDM);
          setSelectedMembersForChat([]);
          setShowStartChatDialog(false);
          return;
        }

        // Create new DM
        const res = await axios.post('/api/v1/conversations', {
          name: `${user.name} & ${selectedMembersForChat[0].name}`,
          type: 'direct',
          memberIds: [selectedMembersForChat[0]._id]
        });

        const newDM = {
          ...res.data,
          members: res.data.members || []
        };

        setConversations(prev => ({
          ...prev,
          directMessages: [...(prev.directMessages || []), newDM]
        }));

        setSelectedConversation(newDM);
      } else {
        // For multiple members, create a group chat
        const memberNames = selectedMembersForChat.map(m => m.name).join(', ');
        const groupName = `${user.name}, ${memberNames}`;

        const res = await axios.post('/api/v1/conversations', {
          name: groupName,
          type: 'group',
          memberIds: selectedMembersForChat.map(m => m._id)
        });

        const newGroup = {
          ...res.data,
          members: res.data.members || []
        };

        setConversations(prev => ({
          ...prev,
          channels: [...(prev.channels || []), newGroup]
        }));

        setSelectedConversation(newGroup);
      }

      setSelectedMembersForChat([]);
      setShowStartChatDialog(false);
    } catch (error) {
      console.error('Error creating chat:', error);
      alert(error.response?.data?.message || 'Failed to create chat');
    }
  };

  const formatMessage = (content) => {
    if (!content) return '';
    
    return content
      // Bold text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Italic text
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      // Code blocks
      .replace(/```(.*?)```/g, '<code>$1</code>')
      // Inline code
      .replace(/`(.*?)`/g, '<code>$1</code>')
      // Links
      .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>')
      // Mentions
      .replace(/@(\w+)/g, '<span class="mention">@$1</span>');
  };

  const handleStartNewChat = () => {
    setShowStartChatDialog(true);
  };

  // Add message grouping helper
  const groupMessagesByDate = (messages) => {
    const groups = {};
    messages.forEach(message => {
      if (!message?.createdAt) {
        // Handle messages without timestamp
        const unknownDateKey = 'Unknown';
        if (!groups[unknownDateKey]) {
          groups[unknownDateKey] = [];
        }
        groups[unknownDateKey].push(message);
        return;
      }

      const date = new Date(message.createdAt);
      if (isNaN(date.getTime())) {
        // Handle invalid dates
        const unknownDateKey = 'Unknown';
        if (!groups[unknownDateKey]) {
          groups[unknownDateKey] = [];
        }
        groups[unknownDateKey].push(message);
        return;
      }

      // Use local date string for grouping
      const dateKey = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(message);
    });
    return groups;
  };

  // Format timestamp
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Format date header
  const formatDateHeader = (dateStr) => {
    if (dateStr === 'Unknown') return 'Unknown Date';
    
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Format all dates to compare only the date part (ignoring time)
    const dateString = date.toLocaleDateString('en-US');
    const todayString = today.toLocaleDateString('en-US');
    const yesterdayString = yesterday.toLocaleDateString('en-US');

    if (dateString === todayString) {
      return 'Today';
    } else if (dateString === yesterdayString) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString([], { 
        weekday: 'long', 
        month: 'long', 
        day: 'numeric',
        year: 'numeric'
      });
    }
  };

  if (loading) {
    return (
      <Container maxWidth={false} disableGutters sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', px: 2 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth={false} disableGutters sx={{ 
      height: 'calc(100vh - 88px)', 
      mt: 3, 
      px: 2,
      '& .MuiPaper-root': {
        backgroundColor: theme => theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.paper, 0.6)
          : theme.palette.background.paper
      }
    }}>
      {/* Legacy Call Panel (hidden) */}
      {FEATURE_FLAGS.messagesVideoCall && SHOW_LEGACY_CALL_PANEL && (
      <Paper elevation={0} sx={{ 
        p: 1.25, 
        mb: 2, 
        borderRadius: 1,
        backgroundColor: theme => theme.palette.mode === 'dark'
          ? alpha('#2a2a2a', 0.8)
          : '#fff',
        border: theme => theme.palette.mode === 'dark'
          ? `1px solid ${alpha(theme.palette.divider, 0.3)}`
          : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: theme => theme.palette.mode === 'dark'
          ? '0 4px 12px rgba(0,0,0,0.4)'
          : '0 2px 8px rgba(0,0,0,0.06)',
        position: 'relative' 
      }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2" sx={{ letterSpacing: 0.4, fontWeight: 600 }}>Call</Typography>
            <Chip size="small" label={inCall ? `In Call` : 'Idle'} color={inCall ? 'success' : 'default'} />
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {!inCall ? (
              <Tooltip title="Join (conversation)"><span>
                <IconButton color="primary" size="small" onClick={joinCall} disabled={!selectedConversation}>
                  <CamIcon fontSize="small" />
                </IconButton>
              </span></Tooltip>
            ) : (
              <Tooltip title="Leave (Esc)">
                <IconButton color="error" size="small" onClick={leaveCall}>
                  <CallEndIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={isMicOn ? 'Mute (M)' : 'Unmute (M)'}>
              <span>
                <IconButton size="small" onClick={toggleMic} disabled={!inCall}>
                  {isMicOn ? <MicIcon fontSize="small" /> : <MicOffIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={isCamOn ? 'Turn camera off (V)' : 'Turn camera on (V)'}>
              <span>
                <IconButton size="small" onClick={toggleCam} disabled={!inCall}>
                  {isCamOn ? <CamIcon fontSize="small" /> : <CamOffIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={isSharing ? 'Sharing...' : 'Share screen (S)'}>
              <span>
                <IconButton size="small" onClick={shareScreen} disabled={!inCall || isSharing}>
                  {isSharing ? <StopShareIcon fontSize="small" /> : <ShareIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={showSelfView ? 'Hide self view' : 'Show self view'}>
              <span>
                <IconButton size="small" onClick={() => setShowSelfView(v => !v)} disabled={!inCall}>
                  {showSelfView ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
        <Box sx={{ mt: 1.25, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 1.25 }}>
          {Object.entries(remoteStreams).map(([pid, stream]) => (
            <VideoTile key={pid} stream={stream} label={`Peer ${pid.slice(-4)}`} />
          ))}
        </Box>
        {inCall && showSelfView && localStreamRef.current ? (
          <Box sx={{ position: 'absolute', right: 12, bottom: 12 }}>
            <Box sx={{
              position: 'relative',
              width: 180,
              aspectRatio: '16 / 9',
              bgcolor: '#000',
              borderRadius: 1.25,
              overflow: 'hidden',
              border: (t) => `1px solid ${alpha(t.palette.divider, 0.5)}`,
              boxShadow: (t) => t.shadows[4],
            }}>
              <video ref={localVideoRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} autoPlay playsInline muted />
              <Box sx={{ position: 'absolute', left: 8, bottom: 8, bgcolor: (t) => alpha(t.palette.common.black, 0.4), color: '#fff', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.75rem' }}>
                You
              </Box>
            </Box>
          </Box>
        ) : null}
      </Paper>
      )}
      <Paper 
        elevation={0}
        sx={{ 
          height: '100%', 
          display: 'flex', 
          overflow: 'hidden',
          borderRadius: 1,
          backgroundColor: theme => theme.palette.mode === 'dark'
            ? alpha('#1a1a1a', 0.8)
            : '#fff',
          border: theme => theme.palette.mode === 'dark'
            ? `1px solid ${alpha(theme.palette.divider, 0.2)}`
            : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: theme => theme.palette.mode === 'dark'
            ? '0 4px 16px rgba(0,0,0,0.4)'
            : '0 2px 8px rgba(0,0,0,0.06)'
        }}
      >
        {/* Sidebar */}
        <StyledSidebar sx={{ width: 280, flexShrink: 0 }}>
          {/* Premium Header */}
          <Box sx={{ 
            p: 2.5,
            pb: 2,
            borderBottom: theme => `2px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -2,
              left: 0,
              width: '60px',
              height: '2px',
              background: theme => `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0)})`,
              borderRadius: 1
            }
          }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700,
                letterSpacing: '-0.5px',
                mb: 1.5,
                background: theme => theme.palette.mode === 'dark'
                  ? `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`
                  : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              Team Chat
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: theme => theme.palette.mode === 'dark'
                    ? alpha(theme.palette.common.white, 0.05)
                    : alpha(theme.palette.common.black, 0.03),
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: theme => theme.palette.mode === 'dark'
                      ? alpha(theme.palette.common.white, 0.08)
                      : alpha(theme.palette.common.black, 0.05),
                  },
                  '&.Mui-focused': {
                    backgroundColor: theme => theme.palette.mode === 'dark'
                      ? alpha(theme.palette.common.white, 0.1)
                      : alpha(theme.palette.common.black, 0.06),
                    boxShadow: theme => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Divider />

          {/* Channels */}
          <List component="nav" dense>
            <ListItem button onClick={() => setChannelsOpen(!channelsOpen)}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {channelsOpen ? <ExpandLessIcon color="action" /> : <ExpandMoreIcon color="action" />}
              </ListItemIcon>
              <ListItemText 
                primary="Channels" 
                primaryTypographyProps={{ 
                  variant: 'subtitle2',
                  color: 'textSecondary'
                }} 
              />
              <Tooltip title="Create new channel">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    setNewConversationOpen(true);
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </ListItem>
            <Collapse in={channelsOpen}>
              {(conversations?.channels || []).map((channel) => {
                if (!channel) return null;
                return (
                  <ListItem
                    key={channel._id}
                    button
                    selected={selectedConversation?._id === channel._id}
                    onClick={() => setSelectedConversation(channel)}
                    sx={{ 
                      pl: 4,
                      borderRadius: 1,
                      mb: 0.5,
                      transition: 'all 0.2s ease',
                      '&.Mui-selected': {
                        backgroundColor: theme => alpha(theme.palette.primary.main, 0.1),
                        '&:hover': {
                          backgroundColor: theme => alpha(theme.palette.primary.main, 0.15),
                        }
                      },
                      '&:hover': {
                        backgroundColor: theme => alpha(theme.palette.action.hover, 0.8),
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {channel.private ? 
                        <LockIcon fontSize="small" color="action" /> : 
                        <TagIcon fontSize="small" color="action" />
                      }
                    </ListItemIcon>
                    <ListItemText 
                      primary={channel.name}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondary={channel?.project?.projectName || null}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        color: 'text.secondary',
                        sx: { mt: 0.25 }
                      }}
                    />
                    {channel.unreadCount > 0 && (
                      <Box
                        sx={{
                          background: theme => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                          color: 'white',
                          borderRadius: 1,
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          minWidth: 22,
                          height: 22,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: theme => `0 2px 8px ${alpha(theme.palette.primary.main, 0.4)}`,
                          animation: 'pulse 2s ease-in-out infinite',
                          '@keyframes pulse': {
                            '0%, 100%': { transform: 'scale(1)' },
                            '50%': { transform: 'scale(1.05)' }
                          },
                          px: 0.8,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                          ml: 1,
                          transition: 'all 0.2s ease',
                          transform: 'scale(1)',
                          '&:hover': {
                            transform: 'scale(1.05)'
                          }
                        }}
                      >
                        {channel.unreadCount}
                      </Box>
                    )}
                  </ListItem>
                );
              })}
            </Collapse>

            {/* Direct Messages */}
            <ListItem button onClick={() => setDirectMessagesOpen(!directMessagesOpen)}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {directMessagesOpen ? 
                  <ExpandLessIcon color="action" /> : 
                  <ExpandMoreIcon color="action" />
                }
              </ListItemIcon>
              <ListItemText 
                primary="Direct Messages" 
                primaryTypographyProps={{ 
                  variant: 'subtitle2',
                  color: 'textSecondary'
                }}
              />
            </ListItem>
            <Collapse in={directMessagesOpen}>
              {(conversations?.directMessages || []).map((dm) => {
                if (!dm || !dm.members) return null;
                const otherMember = (dm.members || []).find(m => m._id !== user._id);
                if (!otherMember) return null;
                
                return (
                  <ListItem
                    key={dm._id}
                    button
                    selected={selectedConversation?._id === dm._id}
                    onClick={() => setSelectedConversation(dm)}
                    sx={{ 
                      pl: 4,
                      borderRadius: 1,
                      mb: 0.5,
                      transition: 'all 0.2s ease',
                      '&.Mui-selected': {
                        backgroundColor: theme => alpha(theme.palette.secondary.main, 0.1),
                        '&:hover': {
                          backgroundColor: theme => alpha(theme.palette.secondary.main, 0.15),
                        }
                      },
                      '&:hover': {
                        backgroundColor: theme => alpha(theme.palette.action.hover, 0.8),
                      }
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: 32 }}>
                      <Avatar sx={{ width: 24, height: 24 }}>
                        {otherMember.name?.[0] || '?'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={otherMember.name || 'Unknown User'}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondary={dm?.project?.projectName || null}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        color: 'text.secondary',
                        sx: { mt: 0.25 }
                      }}
                    />
                    {dm.unreadCount > 0 && (
                      <Box
                        sx={{
                          bgcolor: 'secondary.main',
                          color: 'secondary.contrastText',
                          borderRadius: 1,
                          fontSize: '0.7rem',
                          fontWeight: 'bold',
                          minWidth: 20,
                          height: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          px: 0.8,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                          ml: 1,
                          transition: 'all 0.2s ease',
                          transform: 'scale(1)',
                          '&:hover': {
                            transform: 'scale(1.05)'
                          }
                        }}
                      >
                        {dm.unreadCount}
                      </Box>
                    )}
                  </ListItem>
                );
              })}
            </Collapse>
          </List>
        </StyledSidebar>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex' }}>
          {/* Messages Area */}
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: (theme) => theme.palette.mode === 'dark' ? '#121212' : '#ffffff'
            }}
          >
            {selectedConversation ? (
              <>
                {/* Conversation Header */}
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: theme.palette.background.paper,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    backdropFilter: 'blur(8px)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar
                      sx={{
                        bgcolor: selectedConversation.type === 'group' 
                          ? alpha(theme.palette.primary.main, 0.1)
                          : alpha(theme.palette.secondary.main, 0.1),
                        color: selectedConversation.type === 'group' 
                          ? theme.palette.primary.main 
                          : theme.palette.secondary.main,
                        width: 40,
                        height: 40
                      }}
                    >
                      {selectedConversation.type === 'group' 
                        ? <TagIcon /> 
                        : selectedConversation.members?.find(m => m._id !== user._id)?.name?.[0] || '?'}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        {selectedConversation.type === 'group' ? '#' : '@'}{selectedConversation.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedConversation.members?.length || 0} members • {selectedConversation.type === 'group' ? 'Channel' : 'Direct Message'}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        {selectedConversation?.project?.projectName && (
                          <Chip
                            label={selectedConversation.project.projectName}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 22,
                              borderColor: (theme) => alpha(theme.palette.divider, 0.4),
                              color: 'text.secondary',
                              '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' }
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {FEATURE_FLAGS.messagesVideoCall && (
                    <Tooltip title={inCall ? 'Leave call (Esc)' : (popoutEnabled ? 'Start/Join in pop-out window' : 'Start/Join call') }>
                      <span>
                        <IconButton
                          size="small"
                          color={inCall ? 'error' : 'primary'}
                          onClick={() => {
                            if (inCall) {
                              leaveCall();
                            } else {
                              emitInvite();
                              if (popoutEnabled) {
                                openCallWindow();
                              } else {
                                joinCall();
                              }
                            }
                          }}
                          disabled={!selectedConversation}
                        >
                          {inCall ? <CallEndIcon fontSize="small" /> : <CamIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    )}
                    <Tooltip title={popoutEnabled ? 'Open in new window: On' : 'Open in new window: Off'}>
                      <IconButton 
                        size="small"
                        onClick={() => {
                          setPopoutEnabled(v => { const nv = !v; localStorage.setItem('vc_popout', String(nv)); return nv; });
                        }}
                        sx={{ color: popoutEnabled ? 'primary.main' : 'text.secondary' }}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => setAddMembersOpen(true)}
                      startIcon={<PersonAddIcon />}
                      sx={{ 
                        textTransform: 'none',
                        borderRadius: 1.5,
                        boxShadow: 'none',
                        '&:hover': {
                          boxShadow: 1
                        }
                      }}
                    >
                      Add People
                    </Button>
                    <Button
                      variant="outlined"
                      color="inherit"
                      size="small"
                      onClick={() => setLeaveDialogOpen(true)}
                      startIcon={<ExitToAppIcon />}
                      sx={{
                        textTransform: 'none',
                        borderRadius: 1.5
                      }}
                    >
                      Leave
                    </Button>
                    <Tooltip title="More options">
                      <IconButton 
                        size="small"
                        onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                        sx={{ color: 'text.secondary' }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Inline Call Grid (integrated above messages) */}
                {FEATURE_FLAGS.messagesVideoCall && inCall && !popoutEnabled ? (
                  <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 1.25 }}>
                      {Object.entries(remoteStreams).map(([pid, stream]) => (
                        <VideoTile key={pid} stream={stream} label={`Peer ${pid.slice(-4)}`} />
                      ))}
                    </Box>
                    {showSelfView && localStreamRef.current ? (
                      <Box sx={{ mt: 1.25, display: 'inline-block' }}>
                        <Box sx={{
                          position: 'relative', width: 220, aspectRatio: '16 / 9', bgcolor: '#000', borderRadius: 1.25, overflow: 'hidden', border: (t) => `1px solid ${alpha(t.palette.divider, 0.5)}`
                        }}>
                          <video ref={localVideoRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} autoPlay playsInline muted />
                          <Box sx={{ position: 'absolute', left: 8, bottom: 8, bgcolor: (t) => alpha(t.palette.common.black, 0.4), color: '#fff', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.75rem' }}>You</Box>
                        </Box>
                      </Box>
                    ) : null}
                  </Box>
                ) : null}

                {/* Messages */}
                <Box 
                  sx={{ 
                    flexGrow: 1, 
                    overflow: 'auto', 
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    scrollBehavior: 'smooth',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                      backgroundColor: 'transparent'
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: theme => alpha(theme.palette.primary.main, 0.2),
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: theme => alpha(theme.palette.primary.main, 0.3)
                      }
                    },
                    transition: 'all 0.3s ease'
                  }}
                  ref={messagesEndRef}
                >
                  {Object.entries(groupMessagesByDate(messages)).map(([date, dateMessages]) => (
                    <Box key={date}>
                      {/* Date Divider */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          my: 3,
                          position: 'relative'
                        }}
                      >
                        <Divider sx={{ flex: 1, borderColor: theme => alpha(theme.palette.divider, 0.6) }} />
                        <Typography
                          variant="caption"
                          sx={{
                            px: 2,
                            py: 0.5,
                            bgcolor: theme => alpha(theme.palette.background.paper, 0.8),
                            color: 'text.secondary',
                            borderRadius: 1.5,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            border: '1px solid',
                            borderColor: 'divider'
                          }}
                        >
                          {formatDateHeader(date)}
                        </Typography>
                        <Divider sx={{ flex: 1, borderColor: theme => alpha(theme.palette.divider, 0.6) }} />
                      </Box>

                      {/* Messages for this date */}
                      {dateMessages.map((message, index) => {
                        // Invite banner rendering
                        if (message.type === 'invite') {
                          return (
                            <Box key={message._id} sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                              <Paper variant="outlined" sx={{ px: 2, py: 1, bgcolor: (t) => alpha(t.palette.info.main, 0.06), borderColor: (t) => alpha(t.palette.info.main, 0.3) }}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {message.inviterName || 'Someone'} invited you to a video call
                                  </Typography>
                                  {FEATURE_FLAGS.messagesVideoCall ? (
                                    <>
                                      <Button size="small" variant="contained" onClick={() => {
                                        if (popoutEnabled) {
                                          openCallWindow();
                                        } else {
                                          joinCall();
                                        }
                                        try {
                                          const room = selectedConversation?._id;
                                          const payload = { room, responderId: user?._id, responderName: user?.name, accepted: true };
                                          if (watchSocketRef.current?.connected) watchSocketRef.current.emit('rtc:invite-response', payload);
                                          else if (socketRef.current?.connected) socketRef.current.emit('rtc:invite-response', payload);
                                        } catch {}
                                      }}>Accept</Button>
                                      <Button size="small" variant="text" color="inherit" onClick={() => {
                                        try {
                                          const room = selectedConversation?._id;
                                          const payload = { room, responderId: user?._id, responderName: user?.name, accepted: false };
                                          if (watchSocketRef.current?.connected) watchSocketRef.current.emit('rtc:invite-response', payload);
                                          else if (socketRef.current?.connected) socketRef.current.emit('rtc:invite-response', payload);
                                        } catch {}
                                      }}>Decline</Button>
                                    </>
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">
                                      Video calling disabled
                                    </Typography>
                                  )}
                                </Stack>
                              </Paper>
                            </Box>
                          );
                        }
                        const senderId = typeof message.sender === 'string' ? message.sender : message.sender?._id;
                        const senderEmail = (message.sender && typeof message.sender === 'object') ? message.sender.email : undefined;
                        const isCurrentUser = (
                          senderId === user._id ||
                          (!!senderEmail && !!user?.email && String(senderEmail).toLowerCase() === String(user.email).toLowerCase())
                        );
                        const showAvatar = index === 0 || 
                          dateMessages[index - 1].sender._id !== message.sender._id;
                        const showHeader = showAvatar;

                        return (
                          <Box
                            key={message._id}
                            sx={{
                              display: 'flex',
                              flexDirection: isCurrentUser ? 'row-reverse' : 'row',
                              gap: 1.5,
                              mb: 1
                            }}
                          >
                            {/* Avatar */}
                            <Box sx={{ width: 40, flexShrink: 0 }}>
                              {showAvatar && (
                                <Tooltip title={message.sender.name}>
                                  <Avatar
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      bgcolor: isCurrentUser ? 'primary.main' : 'grey.400',
                                      fontSize: '0.875rem'
                                    }}
                                  >
                                    {message.sender.name[0].toUpperCase()}
                                  </Avatar>
                                </Tooltip>
                              )}
                            </Box>

                            {/* Message Content */}
                            <Box
                              sx={{
                                // Make content take the full message row width
                                width: '100%',
                                maxWidth: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.75
                              }}
                            >
                              {/* Sender Name & Time */}
                              {showHeader && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 1,
                                    color: 'text.secondary'
                                  }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      fontWeight: 500,
                                      color: isCurrentUser ? 'primary.main' : 'text.primary'
                                    }}
                                  >
                                    {isCurrentUser ? 'You' : message.sender.name}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: 'text.secondary' }}
                                  >
                                    {formatMessageTime(message.createdAt)}
                                  </Typography>
                                </Box>
                              )}

                              {/* Message Bubble with Hover Actions */}
                              <Box
                                sx={{
                                  position: 'relative',
                                  '&:hover .message-actions': {
                                    opacity: 1
                                  }
                                }}
                              >
                                <MessageBubble
                                  isCurrentUser={isCurrentUser}
                                  elevation={1}
                                >
                                  <Typography
                                    variant="body2"
                                    component="div"
                                    className="message-content"
                                    sx={{ 
                                      wordBreak: 'break-word',
                                      overflowWrap: 'anywhere',
                                      whiteSpace: 'pre-wrap',
                                      '& a': {
                                        color: isCurrentUser ? 'inherit' : 'primary.main',
                                        textDecoration: 'underline',
                                        '&:hover': {
                                          opacity: 0.8
                                        }
                                      },
                                      '& strong': {
                                        fontWeight: 600
                                      },
                                      '& code': {
                                        bgcolor: isCurrentUser ? alpha(theme.palette.primary.dark, 0.3) : alpha(theme.palette.grey[200], 0.7),
                                        color: isCurrentUser ? 'inherit' : 'text.primary',
                                        p: 0.5,
                                        borderRadius: 0.5,
                                        fontFamily: 'monospace'
                                      }
                                    }}
                                    dangerouslySetInnerHTML={{
                                      __html: formatMessage(message.content)
                                    }}
                                  />
                                  {(() => {
                                    const atts = (message.attachments && message.attachments.length > 0)
                                      ? message.attachments
                                      : (message.attachment ? [message.attachment] : []);
                                    if (!atts.length) return null;
                                    return (
                                      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {atts.map((att, idx) => (
                                          <Box key={(att.url || att.name || 'att') + idx}>
                                            {(att.type === 'image' || att.type?.startsWith('image/') || att.url?.startsWith('blob:')) ? (
                                              <Box sx={{
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 1,
                                                overflow: 'hidden',
                                                boxShadow: 1,
                                                // Consistent image box size caps
                                                maxWidth: 360,
                                                maxHeight: 260,
                                                width: '100%'
                                              }}>
                                                <a href={att.url} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>
                                                  <img
                                                    src={att.url}
                                                    alt={att.name || 'attachment'}
                                                    style={{ display: 'block', width: '100%', height: '100%', maxHeight: 260, objectFit: 'contain' }}
                                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                  />
                                                </a>
                                                {/* caption hidden per request */}
                                              </Box>
                                            ) : (
                                              <Button
                                                variant="outlined"
                                                size="small"
                                                href={att.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ textTransform: 'none' }}
                                              >
                                                {att.name || 'Attachment'}
                                              </Button>
                                            )}
                                          </Box>
                                        ))}
                                      </Box>
                                    );
                                  })()}
                                  {isCurrentUser && (
                                    <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
                                      <Button
                                        size="small"
                                        variant="text"
                                        color="error"
                                        onClick={() => {
                                        if (window.confirm('Delete this message? This cannot be undone.')) {
                                          handleDeleteMessage(message._id);
                                        }
                                      }}
                                        disabled={message.deleting}
                                        sx={{ textTransform: 'none', fontSize: 12, opacity: 0.85, minWidth: 0, p: 0 }}
                                      >
                                        Delete
                                      </Button>
                                    </Box>
                                  )}
                                  {message.error && (
                                    <Typography variant="caption" className="error-message">
                                      {message.errorMessage || 'Error sending message'}
                                    </Typography>
                                  )}
                                </MessageBubble>
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  ))}
                  <div ref={messagesEndRef} />
                </Box>


                {/* Enhanced Message Input */}
                {(FEATURE_FLAGS.messagesVideoCall && Object.keys(callPeers || {}).length > 0 && !inCall) ? (
                  <Paper elevation={0} sx={{ mx: 2, mb: 1, p: 1, border: '1px solid', borderColor: 'divider', bgcolor: (t) => alpha(t.palette.warning.main, 0.06), display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Call in progress — Join?</Typography>
                    <Button size="small" variant="contained" onClick={joinCall}>Join</Button>
                  </Paper>
                ) : null}
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    borderTop: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    position: 'relative'
                  }}
                >
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    handleSendMessage();
                  }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <MessageInput
                        fullWidth
                        multiline
                        maxRows={4}
                        value={newMessage}
                        onChange={handleMessageChange}
                        disabled={sendingMessage}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey && !sendingMessage) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        placeholder={`Message ${selectedConversation.type === 'group' ? '#' + selectedConversation.name : selectedConversation.members.find(m => m._id !== user._id)?.name || 'user'}`}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {sendingMessage ? (
                                <CircularProgress size={24} color="primary" />
                              ) : (
                                <IconButton 
                                  color="primary" 
                                  onClick={handleSendMessage}
                                  disabled={(!newMessage.trim() && !pendingAttachment) || sendingMessage}
                                  sx={{
                                    bgcolor: (newMessage.trim() || pendingAttachment) ? theme.palette.primary.main : 'transparent',
                                    color: (newMessage.trim() || pendingAttachment) ? theme.palette.primary.contrastText : theme.palette.action.disabled,
                                    width: 40,
                                    height: 40,
                                    transition: 'all 0.2s ease',
                                    transform: (newMessage.trim() || pendingAttachment) ? 'scale(1)' : 'scale(0.9)',
                                    '&:hover': {
                                      bgcolor: (newMessage.trim() || pendingAttachment) ? theme.palette.primary.dark : 'transparent',
                                      transform: (newMessage.trim() || pendingAttachment) ? 'scale(1.05)' : 'scale(0.9)',
                                    },
                                    '&:active': {
                                      transform: 'scale(0.95)'
                                    },
                                    boxShadow: (newMessage.trim() || pendingAttachment) ? '0 2px 5px rgba(0,0,0,0.2)' : 'none'
                                  }}
                                >
                                  <SendIcon fontSize="small" />
                                </IconButton>
                              )}
                            </InputAdornment>
                          ),
                          sx: {
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.background.default, 0.8),
                            '&:hover': {
                              bgcolor: alpha(theme.palette.background.default, 1)
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.divider
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.text.secondary
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.primary.main,
                              borderWidth: 1
                            }
                          }
                        }}
                      />
                      {pendingAttachment && (
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 1,
                          py: 0.5,
                          border: '1px dashed',
                          borderColor: 'divider',
                          borderRadius: 1.5,
                          backgroundColor: (t) => alpha(t.palette.background.default, 0.6)
                        }}>
                          {pendingAttachment.file.type?.startsWith('image/') ? (
                            <Box sx={{ width: 48, height: 36, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                              <img src={pendingAttachment.url} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                            </Box>
                          ) : null}
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {pendingAttachment.file.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {Math.round(pendingAttachment.file.size / 1024)} KB
                            </Typography>
                          </Box>
                          <Tooltip title="Remove attachment">
                            <IconButton size="small" onClick={removePendingAttachment}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                      
                      {/* Formatting Toolbar */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Insert emoji">
                            <IconButton
                              size="small"
                              onClick={(event) => setEmojiAnchorEl(event.currentTarget)}
                              sx={{
                                color: theme.palette.mode === 'dark' ? 'grey.200' : 'text.secondary'
                              }}
                            >
                              <EmojiIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Popover
                            open={Boolean(emojiAnchorEl)}
                            anchorEl={emojiAnchorEl}
                            onClose={() => {
                              setEmojiAnchorEl(null);
                              setEmojiSearch('');
                            }}
                            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            PaperProps={{
                              sx: {
                                width: 320,
                                borderRadius: 3,
                                boxShadow: theme.palette.mode === 'dark'
                                  ? '0 20px 48px rgba(0,0,0,0.45)'
                                  : '0 18px 36px rgba(15,23,42,0.16)',
                                border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.35 : 0.12)}`
                              }
                            }}
                          >
                            <Box sx={{ p: 2, pt: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              <TextField
                                value={emojiSearch}
                                onChange={(e) => setEmojiSearch(e.target.value)}
                                placeholder="Search emojis"
                                size="small"
                                autoFocus
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                  ),
                                  sx: {
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'dark'
                                      ? alpha(theme.palette.background.paper, 0.9)
                                      : '#f5f7fb'
                                  }
                                }}
                              />

                              <Tabs
                                value={emojiCategory}
                                onChange={(_evt, value) => setEmojiCategory(value)}
                                variant="scrollable"
                                scrollButtons
                                allowScrollButtonsMobile
                                sx={{
                                  minHeight: 36,
                                  '& .MuiTab-root': {
                                    minHeight: 36,
                                    textTransform: 'none',
                                    fontSize: 12,
                                    px: 1.5
                                  }
                                }}
                              >
                                <Tab label="Smileys" value="smileys" />
                                <Tab label="Gestures" value="gestures" />
                                <Tab label="Nature" value="nature" />
                                <Tab label="Activities" value="activities" />
                                <Tab label="Symbols" value="symbols" />
                              </Tabs>

                              <Box
                                sx={{
                                  maxHeight: 220,
                                  overflow: 'auto',
                                  pr: 0.5,
                                  '&::-webkit-scrollbar': {
                                    width: 6
                                  },
                                  '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.35),
                                    borderRadius: 999
                                  }
                                }}
                              >
                                <Grid container spacing={0.75}>
                                  {filteredEmojis.map(({ char, label }) => (
                                    <Grid item xs={3} key={`${label}-${char}`}>
                                      <Tooltip title={label} arrow placement="top">
                                        <IconButton
                                          onClick={() => handleEmojiSelect(char)}
                                          sx={{
                                            width: '100%',
                                            height: 44,
                                            borderRadius: 2,
                                            fontSize: 24,
                                            transition: 'transform 0.15s ease, background-color 0.15s ease',
                                            '&:hover': {
                                              transform: 'scale(1.08)'
                                            }
                                          }}
                                        >
                                          {char}
                                        </IconButton>
                                      </Tooltip>
                                    </Grid>
                                  ))}

                                  {!filteredEmojis.length && (
                                    <Grid item xs={12}>
                                      <Box sx={{
                                        py: 4,
                                        textAlign: 'center',
                                        color: 'text.secondary'
                                      }}>
                                        <Typography variant="body2">No emojis found for "{emojiSearch}"</Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                </Grid>
                              </Box>
                            </Box>
                          </Popover>
                          <Tooltip title="Attach a file">
                            <IconButton size="small" color="inherit" onClick={handleAttachClick}>
                              <AttachFileIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <input
                            ref={attachInputRef}
                            type="file"
                            hidden
                            onChange={handleAttachmentChange}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Tooltip title="Send message">
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={handleSendMessage}
                                disabled={sendingMessage}
                                sx={{ textTransform: 'none', borderRadius: 1.25 }}
                              >
                                Send
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                  </form>
                </Paper>
              </>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100%',
                gap: 2
              }}>
                <MovieIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                <Typography variant="h6" color="text.secondary">
                  Select a conversation to start messaging
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleStartNewChat}
                  sx={{ 
                    m: 2,
                    textTransform: 'none',
                    borderRadius: 1.5,
                    boxShadow: 'none',
                    '&:hover': { boxShadow: 'none' }
                  }}
                >
                  New Message
                </Button>
              </Box>
            )}
          </Box>

          {/* Members Sidebar */}
          {selectedConversation && (
            <Box 
              sx={{ 
                width: 280,
                borderLeft: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box sx={{ 
                p: 2.5,
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  {selectedConversation.type === 'group' ? 'Channel Members' : 'Participants'}
                  <Typography 
                    component="span" 
                    color="text.secondary" 
                    sx={{ ml: 1, fontSize: '0.875rem' }}
                  >
                    ({selectedConversation.members?.length || 0})
                  </Typography>
                </Typography>
                {selectedMembersForChat.length > 0 && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleStartChatWithSelected}
                    sx={{ 
                      textTransform: 'none',
                      borderRadius: 1.5,
                      boxShadow: 'none',
                      '&:hover': { boxShadow: 'none' }
                    }}
                  >
                    Start Chat ({selectedMembersForChat.length})
                  </Button>
                )}
              </Box>
              <List sx={{ 
                flexGrow: 1, 
                overflow: 'auto',
                px: 1
              }}>
                {(selectedConversation.members || []).map((member) => (
                  <ListItem
                    key={member._id}
                    onClick={() => handleMemberSelect(member)}
                    sx={{ 
                      cursor: 'pointer',
                      borderRadius: 1,
                      mb: 0.5,
                      bgcolor: selectedMembersForChat.some(m => m._id === member._id) 
                        ? 'action.selected' 
                        : 'transparent',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar 
                        sx={{ 
                          bgcolor: 'grey.400',
                          width: 32,
                          height: 32
                        }}
                      >
                        {member.name?.[0]?.toUpperCase() || '?'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={member.name}
                      secondary={member.role || 'Crew Member'}
                      primaryTypographyProps={{ 
                        variant: 'body1',
                        fontWeight: 500
                      }}
                      secondaryTypographyProps={{ 
                        variant: 'body2',
                        color: 'text.secondary'
                      }}
                    />
                    {selectedMembersForChat.some(m => m._id === member._id) ? (
                      <CheckCircleIcon 
                        color="primary" 
                        sx={{ 
                          fontSize: 20,
                          ml: 1
                        }} 
                      />
                    ) : (
                      <UncheckedIcon 
                        sx={{ 
                          fontSize: 20,
                          ml: 1,
                          color: 'text.secondary'
                        }} 
                      />
                    )}
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Add Members Dialog */}
      <Dialog 
        open={addMembersOpen} 
        onClose={() => {
          setAddMembersOpen(false);
          setSelectedMembers([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Members to {selectedConversation?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Autocomplete
              multiple
              options={(availableCrewMembers || []).filter(member =>
                !(selectedConversation?.members || []).some(m => m._id === member._id)
              )}
              getOptionLabel={(option) => `${option.name} (${option.role} - ${option.department})`}
              value={selectedMembers}
              onChange={(event, newValue) => {
                setSelectedMembers(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Select members"
                  placeholder="Search members..."
                />
              )}
              renderOption={(props, option) => (
                <MenuItem {...props}>
                  <ListItemAvatar>
                    <Avatar>
                      {option.name[0]}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={option.name}
                    secondary={`${option.role} - ${option.department}`}
                  />
                </MenuItem>
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    label={`${option.name} (${option.role})`}
                    {...getTagProps({ index })}
                    avatar={<Avatar>{option.name[0]}</Avatar>}
                  />
                ))
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddMembersOpen(false);
            setSelectedMembers([]);
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleAddMembers(selectedMembers.map(member => member._id))}
            disabled={selectedMembers.length === 0}
          >
            Add Members
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Conversation Dialog */}
      <Dialog
        open={newConversationOpen}
        onClose={() => {
          setNewConversationOpen(false);
          setConversationName('');
          setSelectedMembers([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Channel</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Channel Name"
              value={conversationName}
              onChange={(e) => setConversationName(e.target.value)}
              placeholder="e.g., camera-department, production-team"
              helperText="Use lowercase letters, numbers, and hyphens"
            />
            <Autocomplete
              multiple
              options={availableCrewMembers || []}
              getOptionLabel={(option) => `${option.name} (${option.role} - ${option.department})`}
              value={selectedMembers}
              onChange={(event, newValue) => {
                setSelectedMembers(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Select members"
                  placeholder="Search members..."
                />
              )}
              renderOption={(props, option) => (
                <MenuItem {...props}>
                  <ListItemAvatar>
                    <Avatar>
                      {option.name[0]}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={option.name}
                    secondary={`${option.role} - ${option.department}`}
                  />
                </MenuItem>
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    label={`${option.name} (${option.role})`}
                    {...getTagProps({ index })}
                    avatar={<Avatar>{option.name[0]}</Avatar>}
                  />
                ))
              }
              groupBy={(option) => option.department}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setNewConversationOpen(false);
            setConversationName('');
            setSelectedMembers([]);
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateConversation}
            disabled={!conversationName.trim() || selectedMembers.length === 0}
          >
            Create Channel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
      >
        {selectedConversation?.type === 'group' && (
          <MenuItem onClick={() => {
            setAddMembersOpen(true);
            setMenuAnchorEl(null);
          }}>
            <ListItemIcon>
              <PersonAddIcon fontSize="small" />
            </ListItemIcon>
            Add Members
          </MenuItem>
        )}
        {selectedConversation?.type === 'group' && (
          <MenuItem 
            onClick={() => {
              setDeleteDialogOpen(true);
              setMenuAnchorEl(null);
            }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            Delete Channel
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          setMenuAnchorEl(null);
          // TODO: Implement mute notifications
        }}>
          <ListItemIcon>
            <NotificationsOffIcon fontSize="small" />
          </ListItemIcon>
          Mute Notifications
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Channel</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the channel "{selectedConversation?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConversation}
            color="error"
            variant="contained"
          >
            Delete Channel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Leave Conversation Dialog */}
      <Dialog
        open={leaveDialogOpen}
        onClose={() => setLeaveDialogOpen(false)}
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Leave {selectedConversation?.type === 'group' ? 'Channel' : 'Conversation'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {selectedConversation?.type === 'group' 
              ? "Are you sure you want to leave this channel? You won't receive any future messages unless you're re-added by another member."
              : "Are you sure you want to leave this conversation? You can always start a new conversation with this person later."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setLeaveDialogOpen(false)}
            sx={{ 
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleLeaveConversation} 
            variant="contained"
            color="error"
            sx={{ 
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' }
            }}
          >
            Leave
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mention Popup */}
      <Popover
        open={Boolean(mentionAnchorEl)}
        anchorEl={mentionAnchorEl}
        onClose={() => setMentionAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <List sx={{ width: 300 }}>
          {crewMembers
            .filter(member => 
              member.name.toLowerCase().includes(mentionSearch.toLowerCase()) ||
              member.role.toLowerCase().includes(mentionSearch.toLowerCase())
            )
            .map(member => (
              <MenuItem 
                key={member._id} 
                onClick={() => handleMentionSelect(member)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Avatar sx={{ width: 24, height: 24 }}>
                  {member.name[0]}
                </Avatar>
                <Box>
                  <Typography variant="body2">
                    {member.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {member.role} - {member.department}
                  </Typography>
                </Box>
                {!(selectedConversation?.members || []).some(m => m._id === member._id) && (
                  <Chip
                    size="small"
                    label="Not in conversation"
                    color="warning"
                    sx={{ ml: 'auto' }}
                  />
                )}
              </MenuItem>
            ))}
        </List>
      </Popover>

      {/* Add Member Prompt */}
      <Dialog
        open={showAddMemberPrompt}
        onClose={() => {
          setShowAddMemberPrompt(false);
          setSelectedMention(null);
        }}
      >
        <DialogTitle>Add to Conversation</DialogTitle>
        <DialogContent>
          <Typography>
            {selectedMention?.name} is not in this conversation. Would you like to add them?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowAddMemberPrompt(false);
            setSelectedMention(null);
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddMentionedMember}
          >
            Add to Conversation
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Chat Dialog */}
      <Dialog
        open={showStartChatDialog}
        onClose={() => {
          setShowStartChatDialog(false);
          setSelectedMembersForChat([]);
        }}
        PaperProps={{
          sx: { 
            borderRadius: 2,
            minWidth: 400
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            New Message
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select crew members to start a conversation
          </Typography>
          <List sx={{ width: '100%' }}>
            {availableCrewMembers.map((member) => (
              <ListItem
                key={member._id}
                onClick={() => handleMemberSelect(member)}
                sx={{ 
                  cursor: 'pointer',
                  borderRadius: 1,
                  mb: 0.5,
                  bgcolor: selectedMembersForChat.some(m => m._id === member._id) 
                    ? 'action.selected' 
                    : 'transparent',
                  '&:hover': {
                    bgcolor: 'action.hover'
                  }
                }}
              >
                <ListItemAvatar>
                  <Avatar 
                    sx={{ 
                      bgcolor: 'grey.400',
                      width: 40,
                      height: 40
                    }}
                  >
                    {member.name?.[0]?.toUpperCase() || '?'}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={member.name}
                  secondary={member.role || 'Crew Member'}
                  primaryTypographyProps={{ 
                    variant: 'body1',
                    fontWeight: 500
                  }}
                  secondaryTypographyProps={{ 
                    variant: 'body2',
                    color: 'text.secondary'
                  }}
                />
                {selectedMembersForChat.some(m => m._id === member._id) ? (
                  <CheckCircleIcon 
                    color="primary" 
                    sx={{ 
                      fontSize: 20,
                      ml: 1
                    }} 
                  />
                ) : (
                  <UncheckedIcon 
                    sx={{ 
                      fontSize: 20,
                      ml: 1,
                      color: 'text.secondary'
                    }} 
                  />
                )}
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setShowStartChatDialog(false);
              setSelectedMembersForChat([]);
            }}
            sx={{ 
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleStartChatWithSelected}
            disabled={selectedMembersForChat.length === 0}
            variant="contained"
            sx={{ 
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' }
            }}
          >
            Start Chat
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Messaging;
